import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';

// CONSTANTS
import { ROUTE_DISCOVER_APP } from '@/constants/routes';

// UTILITY
import { getPlaceholderUrl } from '@/utility/content';
import { formatPrice } from '@/utility/currency';
import getLink from '@/utility/routes';

// COMPONENTS
import RouterLink from '@/components/Link';
import ReactHtml from '@/components/ReactHtml';

// HELP
import ActionsMenu from './ActionsMenu';

// STYLES
import { Container, PriceContainer } from '../styles';

const SearchCardApp = ({ className, data, actions, link, analyticsLocation, ...props }) => {
  const [t] = useTranslation();

  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));

  const to = React.useMemo(
    () =>
      link ||
      getLink(ROUTE_DISCOVER_APP, {
        slug: data.unique_slug || data.id,
      }),
    [link, data.unique_slug, data.id],
  );

  const getPrice = price => {
    return price && Math.ceil(price) !== 0
      ? t('common.costMonth', { cost: formatPrice(price) })
      : t('common.free');
  };

  const renderPrice = () => {
    const priceContent =
      data.deal_price || data.deal_price === 0 ? (
        <div className="price-container__deal-container">
          <span className="price-container__cost">{getPrice(data.price)}</span>
          <span className="price-container__deal-cost">{getPrice(data.deal_price)}</span>
        </div>
      ) : (
        <span className="price-container__cost">{getPrice(data.price)}</span>
      );

    return (
      <PriceContainer className="price-container search-card__month-cost search-card-app__month-cost">
        {priceContent}
      </PriceContainer>
    );
  };

  const getDescription = () => {
    const desc = data.super_short_desc.length
      ? data.super_short_desc?.trim()
      : data.short_desc?.trim();

    return desc.length > 200 ? `${desc.substring(0, 190)}...` : desc;
  };

  const placeholer = React.useMemo(() =>
    getPlaceholderUrl({ width: 126, height: 72, bgColor: 'F5F6F7' }),
  );

  return (
    <Container {...props} className={clsx('search-card', `search-card-app`, className)}>
      <ActionsMenu className="search-card__button" actions={actions} />
      <RouterLink className="search-card__link" to={to} location={analyticsLocation} />

      <div className="search-card__column-group">
        <div className="search-card__column ">
          <div className="search-card__image search-card-app__image">
            <img src={data.thumbnail || placeholer} alt="app-logo" />
          </div>
        </div>

        <div className="search-card__column">
          <Typography className={clsx(downXs ? 'm-b-10' : 'm-b-5')} variant="h5">
            {data.name}
          </Typography>
          {renderPrice()}
        </div>
      </div>
      <div className="search-card__column-group">
        <div className="search-card__column">
          {!downXs && (
            <Typography className="p4 search-card__description" variant="inherit" component="div">
              <ReactHtml className="markdown">{getDescription()}</ReactHtml>
            </Typography>
          )}
        </div>
      </div>
    </Container>
  );
};

SearchCardApp.defaultProps = {
  className: '',
  link: '',
  analyticsLocation: '',
};

SearchCardApp.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
  actions: PropTypes.array.isRequired,
  link: PropTypes.string,
  analyticsLocation: PropTypes.string,
};

export default React.memo(SearchCardApp);
